import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Container, Overline, Reveal, TextLink } from "@/components/primitives";
import { ProductFinalCTA } from "@/components/ProductSections";
import { getPost, posts, lastModified, formatDate } from "@/content/insights";
import { AUTHOR } from "@/config/author";
import { useDocumentMeta, JsonLd, ORGANIZATION, SITE_ORIGIN, DEFAULT_OG_IMAGE } from "@/lib/meta";

function articleJsonLd(post) {
  const url = `${SITE_ORIGIN}/insights/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: url,
    url,
    headline: post.title,
    description: post.dek,
    image: DEFAULT_OG_IMAGE,
    articleSection: post.category,
    inLanguage: "en-GB",
    datePublished: post.published,
    dateModified: lastModified(post),
    author: {
      "@type": "Person",
      name: AUTHOR.name,
      jobTitle: AUTHOR.role,
      description: AUTHOR.credential,
      url: `${SITE_ORIGIN}${AUTHOR.path}`,
    },
    publisher: ORGANIZATION,
  };
}

// Body text can carry links written as [words](/path); site paths use the
// router, anything else opens as an ordinary link.
function RichText({ text }) {
  const parts = [];
  const rx = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [, words, href] = m;
    const cls = "font-semibold text-foreground underline decoration-accent/60 underline-offset-4 transition-colors hover:text-accent";
    parts.push(
      href.startsWith("/") ? (
        <Link key={m.index} to={href} className={cls}>{words}</Link>
      ) : (
        <a key={m.index} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{words}</a>
      ),
    );
    last = rx.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// A body block is a paragraph (plain string), { h2: "..." } or { ul: [...] }.
function BodyBlock({ block }) {
  if (typeof block === "string") {
    return (
      <p className="text-[1.05rem] leading-relaxed text-foreground/90">
        <RichText text={block} />
      </p>
    );
  }
  if (block.h2) {
    return (
      <h2 className="pt-6 font-display text-2xl font-semibold leading-tight tracking-tight text-foreground md:text-3xl">
        {block.h2}
      </h2>
    );
  }
  if (block.ul) {
    return (
      <ul className="list-disc space-y-3 pl-6 text-[1.05rem] leading-relaxed text-foreground/90 marker:text-accent">
        {block.ul.map((item, i) => (
          <li key={i}>
            <RichText text={item} />
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function InsightPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  useDocumentMeta({
    title: post ? post.title : "Insights",
    description: post ? post.dek : undefined,
    path: post ? `/insights/${post.slug}` : "/insights",
    type: "article",
  });
  if (!post) return <Navigate to="/insights" replace />;

  const more = posts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="insight-post-page">
      <JsonLd data={articleJsonLd(post)} />
      {/* Header */}
      <section className="border-b border-border">
        <Container className="py-16 md:py-24">
          <Link
            to="/insights"
            data-testid="insight-back"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-accent"
          >
            <ArrowLeft strokeWidth={1.6} className="h-4 w-4" /> All insights
          </Link>
          <div className="mt-8 flex items-center gap-4">
            <Overline accent>{post.category}</Overline>
            <span className="text-sm text-muted-foreground">{post.readTime}</span>
          </div>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.03] tracking-tight text-foreground md:text-6xl">
            {post.title}
          </h1>
          <div
            data-testid="insight-byline"
            className="mt-8 flex flex-col gap-1 text-sm leading-relaxed text-muted-foreground"
          >
            <p>
              By{" "}
              <Link
                to={AUTHOR.path}
                rel="author"
                className="font-semibold text-foreground transition-colors hover:text-accent"
              >
                {AUTHOR.name}
              </Link>
              , {AUTHOR.role} · {AUTHOR.credential}
            </p>
            <p>
              Published <time dateTime={post.published}>{formatDate(post.published)}</time>
              {lastModified(post) !== post.published && (
                <>
                  {" "}· Last updated{" "}
                  <time dateTime={lastModified(post)}>{formatDate(lastModified(post))}</time>
                </>
              )}
            </p>
          </div>
        </Container>
      </section>

      {/* Body */}
      <section className="border-b border-border">
        <Container className="py-16 md:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <p className="border-l-2 border-accent pl-5 font-display text-2xl font-semibold not-italic leading-snug tracking-[-0.02em] text-foreground md:text-3xl">
                {post.lead}
              </p>
              <div className="mt-10 space-y-6">
                {post.body.map((block, i) => (
                  <BodyBlock key={i} block={block} />
                ))}
              </div>

              {post.sources && post.sources.length > 0 && (
                <div className="mt-12 border-t border-border pt-8" data-testid="insight-sources">
                  <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">Sources</h2>
                  <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
                    {post.sources.map((src) => (
                      <li key={src.url}>
                        <a href={src.url} target="_blank" rel="noopener noreferrer" className="underline decoration-border underline-offset-4 transition-colors hover:text-accent">
                          {src.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-12 border-t border-border pt-8">
                <TextLink to={post.cta.to} testid="insight-inline-cta">
                  {post.cta.label}
                </TextLink>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* More insights */}
      {more.length > 0 && (
        <section className="border-b border-border" data-testid="insight-more">
          <Container className="border-l border-border md:grid md:grid-cols-2">
            {more.map((p) => (
              <Link
                key={p.slug}
                to={`/insights/${p.slug}`}
                className="group block border-b border-r border-border p-8 transition-colors hover:bg-secondary/50 md:p-12"
              >
                <span className="overline text-accent">{p.category}</span>
                <h3 className="mt-4 font-display text-2xl font-medium leading-tight tracking-tight text-foreground transition-colors group-hover:text-accent md:text-3xl">
                  {p.title}
                </h3>
              </Link>
            ))}
          </Container>
        </section>
      )}

      <ProductFinalCTA
        testid="insight-final-cta"
        title="Take back control of how your firm runs."
        body="A blunt, practical walkthrough built around your firm — not a generic demo."
        label="Book a walkthrough"
      />
    </div>
  );
}
